import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, User as UserIcon, Lock, ArrowRight, Sparkles } from 'lucide-react';
import Alert from '../common/Alert';
import GoogleButton from './GoogleButton';

// Human-readable messages for the ?error=… codes the Google callback can
// redirect back with.
const GOOGLE_ERRORS = {
  google_disabled: 'Google sign-in is not enabled on this server.',
  google_denied: 'Google sign-in was cancelled.',
  google_state: 'Your sign-in session expired. Please try again.',
  google_email: 'Your Google account has no verified email address.',
  google_failed: 'Google sign-in failed. Please try again.'
};

const LoginForm = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [totp, setTotp] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Surface an error handed back by the Google OAuth redirect, then drop it
  // from the URL so a refresh doesn't keep showing it.
  useEffect(() => {
    const code = searchParams.get('error');
    if (code) {
      setError(GOOGLE_ERRORS[code] || 'Sign-in failed. Please try again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.username, formData.password, twoFactorRequired ? totp : undefined);
    if (result.success) {
      navigate('/');
    } else if (result.twoFactorRequired) {
      setTwoFactorRequired(true);
      if (totp) setError(result.message); // wrong code entered
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10 items-stretch max-w-6xl mx-auto -my-4 lg:-my-8">
      {/* Form side */}
      <div className="flex items-center justify-center py-4 lg:py-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-700"
            >
              ← Back to home
            </Link>
            <h1 className="mt-6 text-3xl font-bold tracking-tight text-ink-900">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-ink-500">
              Sign in to manage your dynamic links.
            </p>
          </div>

          {error && <Alert type="error" message={error} className="mb-5" />}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="username"
                className="block text-sm font-medium text-ink-700"
              >
                Username
              </label>
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder="yourname"
                  autoComplete="username"
                  className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-3.5 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-ink-700"
                >
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-10 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {twoFactorRequired && (
              <div className="space-y-1.5">
                <label htmlFor="totp" className="block text-sm font-medium text-ink-700">
                  Authentication code
                </label>
                <input
                  type="text"
                  id="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\s/g, ''))}
                  placeholder="6-digit code"
                  autoFocus
                  className="w-full rounded-lg border border-ink-200 bg-white px-3.5 py-2.5 text-sm tracking-widest text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
                <p className="text-xs text-ink-500">Enter the code from your authenticator app.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-ink-800 hover:shadow-card transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : twoFactorRequired ? 'Verify & sign in' : 'Sign in'}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <GoogleButton label="Sign in with Google" />

          <p className="mt-6 text-center text-sm text-ink-500">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-semibold text-brand-600 hover:text-brand-700"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Visual side */}
      <div className="hidden lg:block">
        <div className="relative h-full overflow-hidden rounded-3xl border border-ink-200 bg-ink-900 text-white shadow-pop">
          <div
            className="absolute inset-0 opacity-70 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(600px circle at 30% 20%, rgba(99,102,241,0.55), transparent 60%), radial-gradient(500px circle at 80% 90%, rgba(236,72,153,0.4), transparent 60%)',
            }}
          />
          <div className="relative h-full flex flex-col p-10">
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-medium text-white/80">
              <Sparkles className="h-3.5 w-3.5 text-brand-300" />
              Built for shipping teams
            </div>
            <h2 className="mt-8 text-3xl font-bold tracking-tight leading-tight">
              Track every click,
              <br /> drive every install.
            </h2>
            <p className="mt-4 text-ink-300 max-w-sm">
              Dynalink gives you short URLs, deep links and analytics in one
              clean interface.
            </p>
            <div className="mt-auto grid grid-cols-2 gap-4">
              {[
                ['Avg redirect', '< 40ms'],
                ['Uptime', '99.99%'],
                ['Active links', '∞'],
                ['Setup time', '< 1 min'],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="rounded-xl border border-white/10 bg-white/5 p-4"
                >
                  <p className="text-2xl font-bold tracking-tight">{v}</p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-white/60">
                    {k}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
